import React, { useEffect, useState } from 'react';
import { Box, Button, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { abvText, BEER_AXES, BEER_ITEMS, BeerItem, beerRanges, BeerStyle, BeerWord, ibuText, srmToHex, stylesOf } from '../../utils/beerGuide';
import guide from './GuideSheet.module.css';
import classes from './BeerDetails.module.css';

/** Filled up to lo; lightly up to hi, where a word's styles differ */
const Pips: React.FC<{ range: [number, number] }> = ({ range: [lo, hi] }) => (
  <span className={guide.pips} role="img" aria-label={hi > lo ? `${lo} to ${hi} out of 5, depending on the style` : `${lo} out of 5`}>
    {[1, 2, 3, 4, 5].map((p) => <span key={p} className={`${guide.pip} ${p <= lo ? guide.pipOn : p <= hi ? guide.pipMaybe : ''}`} />)}
  </span>
);

/** Every flavour axis; `words` adds the guide's word for each level ("Assertive"), for one style's ratings */
const Scores: React.FC<{ ranges: [number, number][]; words?: boolean }> = ({ ranges, words }) => (
  <dl className={guide.scores}>
    {BEER_AXES.map((axis, k) => (
      <React.Fragment key={axis.k}>
        <dt>{axis.label}</dt>
        <dd className={classes.score}>
          <Pips range={ranges[k]} />
          {words && <Text component="span" size="xs" c="dimmed">{axis.words[ranges[k][0]]}</Text>}
        </dd>
      </React.Fragment>
    ))}
  </dl>
);

const Swatch: React.FC<{ srm: number }> = ({ srm }) => <span className={classes.swatch} style={{ background: srmToHex(srm) }} aria-hidden="true" />;

const StyleView: React.FC<{ item: BeerStyle; find?: React.ReactNode }> = ({ item, find }) => (
  <Stack gap="sm">
    <Group gap="sm" wrap="nowrap" align="center">
      <Swatch srm={item.srm} />
      {find}
      <Box>
        <Text className={`${guide.ja} ${classes.name}`}>{item.n}</Text>
        <Text size="sm" c="dimmed">{item.f} · {item.o}</Text>
      </Box>
    </Group>
    <Text size="sm">{item.d}</Text>
    <Scores ranges={beerRanges(item)} words />
    <dl className={classes.facts}>
      <dt>ABV</dt><dd>{abvText(item)}</dd>
      <dt>IBU</dt><dd>{ibuText(item)}</dd>
      <dt>Serve at</dt><dd>{item.t}</dd>
      <dt>Glass</dt><dd>{item.g}</dd>
      <dt>Goes with</dt><dd>{item.p}</dd>
      <dt>Examples</dt><dd>{item.x.join(', ')}</dd>
    </dl>
    <Text size="xs" c="dimmed">
      About the style in general, not this brewery’s beer. ABV, IBU and colour follow the BJCP 2021 guidelines; the 0–5 ratings are the guide’s own judgement.
    </Text>
  </Stack>
);

const WordView: React.FC<{ item: BeerWord; onPick: (key: string) => void; find?: React.ReactNode }> = ({ item, onPick, find }) => (
  <Stack gap="sm">
    <Group gap="sm" wrap="nowrap" align="center">
      {find}
      <Box>
        <Text className={`${guide.ja} ${classes.name}`}>{item.n}</Text>
        <Text size="sm" c="dimmed">The menu doesn’t say which kind. {item.note}</Text>
      </Box>
    </Group>
    <Scores ranges={beerRanges(item)} />
    <Text size="xs" c="dimmed">Across the styles below; light dots depend on which one it is.</Text>
    <Text fw={700}>What it could be</Text>
    <ul className={guide.rows}>
      {stylesOf(item).map((s) => (
        <li key={s.key}>
          <UnstyledButton className={guide.row} onClick={() => onPick(s.key)}>
            <Swatch srm={s.srm} />
            <span className={guide.rowMain}>
              <Text component="span" fw={600}>{s.n}</Text>
              <Text component="span" size="xs" c="dimmed">{s.f} · {abvText(s)}</Text>
            </span>
            <IconChevronRight size={16} />
          </UnstyledButton>
        </li>
      ))}
    </ul>
  </Stack>
);

interface BeerDetailsProps {
  /** The styles the tapped line names; none when it's about a beer but names no style */
  items: BeerItem[];
  /** Changes with the tapped line; the view resets */
  resetKey: number;
  /** The thumbnail that finds the line on the menu, shown beside the style's colour */
  find?: React.ReactNode;
}

/**
 * What the beer guide says about a line, shown in the scan sheet: the style's
 * colour, flavour ratings, strength and what it goes with. A tab per name when
 * the line names several; a word like IPA lists the styles it could be.
 */
export const BeerDetails: React.FC<BeerDetailsProps> = ({ items, resetKey, find }) => {
  const [tab, setTab] = useState(0);
  const [sub, setSub] = useState<string | null>(null);

  useEffect(() => {
    setTab(0);
    setSub(null);
  }, [resetKey]);

  if (!items.length) {
    return <Text size="sm" c="dimmed" mt="xs">This line is about a beer, but it doesn’t name a style from the guide.</Text>;
  }

  const at = items[Math.min(tab, items.length - 1)];
  const item = sub ? BEER_ITEMS.get(sub)! : at;
  return (
    <Box>
      {items.length > 1 && (
        <Group gap={6} my="xs" role="tablist">
          {items.map((it, n) => (
            <Button
              key={it.key}
              size="compact-sm"
              radius="xl"
              variant={n === tab && !sub ? 'filled' : 'light'}
              color="orange"
              role="tab"
              aria-selected={n === tab && !sub}
              onClick={() => { setTab(n); setSub(null); }}
            >
              {it.n}
            </Button>
          ))}
        </Group>
      )}
      {sub && (
        <Button variant="subtle" px={0} size="compact-sm" leftSection={<IconChevronLeft size={16} />} onClick={() => setSub(null)}>
          Back to {at.n}
        </Button>
      )}
      <Box mt="xs">
        {item.kind === 'style' ? <StyleView item={item} find={find} /> : <WordView item={item} onPick={setSub} find={find} />}
      </Box>
    </Box>
  );
};
